import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { StructuredText, Image } from "react-datocms";
import { useDispatch } from "react-redux";
import store from "@/redux/store";
import { useSelector } from "react-redux";

const isServer = typeof window === "undefined";
const OwlCarousel = !isServer ? dynamic(import("react-owl-carousel")) : null;

const MetaTags = dynamic(() => import("@/common/MetaTags"));
const InfoSection = dynamic(() => import("@/common/InfoSection"));

const TwoColumnIconBoxCardWithImage = dynamic(() =>
  import("@/components/common/TwoColumnIconBoxCardWithImage")
);
const TwoColumnGradientCard = dynamic(() =>
  import("@/components/common/TwoColumnGradientCard")
);

const SeparatorLine = dynamic(() =>
  import("@/components/common/SeparatorLine")
);
const Map = dynamic(() => import("@/common/Map"));
const Link = dynamic(() => import("@/common/UI/Link"));
const CoursesCard = dynamic(() => import("@/components/cards/CoursesCard"));

const Custom500 = dynamic(() => import("./../../500"));
import {
  fetchSediDetailsData,
  emptySediDetailData,
} from "@/redux/actions/sediPageActions";
import {
  fetchLabelsAndConfigData,
  setLabelsConfigsData,
} from "@/redux/actions/labelsAndConfigActions";

// Import graphQL query variable
import { LABEL_AND_CONFIG_QUERY } from "@/graphql/settingsQuery";
import { SEDI_DETAIL_QUERY } from "@/graphql/sediPageQuery";

export default function LocationDetails(props) {
  const [isShow, setIsShow] = useState(false);
  const [labelsList, setLabelsList] = useState();
  const [siteConfig, setSiteConfig] = useState();

  const dispatch = useDispatch();

  const { pageData, labelsConfigData, sediPageData, noData } = props;

  const options = {
    loop: true,
    margin: 20,
    dots: true,
    autoplay: true,
    responsive: {
      0: {
        items: 1,
      },
      576: {
        items: 2,
      },
    },
  };

  useEffect(() => {
    // Set labelsList local state while changed on redux store data
    dispatch(setLabelsConfigsData(labelsConfigData));
    setLabelsList(labelsConfigData?.labelsList);
    setSiteConfig(labelsConfigData?.siteConfig);
  }, [dispatch, labelsConfigData]);

  useEffect(() => {
    return () => {
      dispatch(emptySediDetailData());
    };
  }, [dispatch]);

  useEffect(() => {
    setTimeout(() => {
      setIsShow(true);
    }, 100);
  }, []);

  const downloadURL = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (noData) {
    return (
      <>
        <Custom500 />
      </>
    );
  }

  return (
    <>
      {isShow && (
        <MetaTags
          metaTags={sediPageData?._seoMetaTags}
          seoData={sediPageData?.seo}
          noFollow={sediPageData?.nofollow}
          noIndex={sediPageData?.noindex}
        />
      )}

      <InfoSection
        title={sediPageData?.title}
        hideLastMenu={true}
        hideTitle={true}
      />

      <section className="pt-[40px] pb-8">
        <div className="grid grid-cols-1 gap-5 md:10 md:mb-16 md:gap-10 ">
          <div className="container">
            <div className="w-full md:w-3/4 mx-auto">
              <div className="mb-[1.5rem]">
                <Link
                  style={{
                    color: siteConfig?.brandButtonDefaultColor?.hex,
                  }}
                  className=" font-extrabold tracking-widest block"
                  href={`/dovesiamo/${pageData?.sediDetailsData?.premisesList?.slug}`}
                >
                  <a className="flex items-center">
                    <iconify-icon
                      icon="ci:chevron-big-left"
                      width={25}
                    ></iconify-icon>
                    <span className="ml-3">{labelsList?.backToPremises}</span>
                  </a>
                </Link>
              </div>
              <h1 className="h1-web font-extrabold w-full md:w-3/4 mb-[10px] md:mb-0">
                {sediPageData?.subtitle}{" "}
              </h1>
            </div>
          </div>
          <section className="h-[60vh] md:h-[70vh] xl:h-[76vh] xxl:h-[50vh]">
            <div className="container-fluid h-full">
              <Map
                mapData={sediPageData?.map}
                iconData={pageData?.sediDetailsData?.premisesList?.premisesIcon}
                siteConfig={siteConfig}
                premisesList={sediPageData?.map?.premises}
                sediDetailPage={true}
                labelsList={labelsList}
              />
            </div>
          </section>
          <div className="container">
            <div className="w-full md:w-3/4 mx-auto">
              <div className="mb-4 h4-web last:mb-0 text-[#333] structure-format mt-8">
                <StructuredText data={sediPageData?.description} />
              </div>
            </div>
          </div>
          {isShow && sediPageData?.gallery?.length > 0 && (
            <div className="container">
              <div className="w-full md:w-3/4 mx-auto">
                <OwlCarousel
                  className="owl-carousel owl-theme slug-slider-carousel"
                  {...options}
                >
                  {sediPageData?.gallery?.map((item, index) => {
                    return (
                      <div
                        className="mb-4 h4-web last:mb-0 text-[#333] structure-format mt-8"
                        key={index}
                      >
                        <Image
                          data={{
                            ...item?.responsiveImage,
                            alt: `Cover Image for ${item?.alt}`,
                          }}
                          alt={`Cover Image for ${item?.alt}`}
                          title={`Cover Image for ${item?.title}`}
                          lazyLoad={true}
                          fadeInDuration={10}
                          priority={true}
                          className="object-set"
                        />
                      </div>
                    );
                  })}
                </OwlCarousel>
              </div>
            </div>
          )}
        </div>
        {sediPageData?.activeCourses?.length > 0 && (
          <div className="container mt-10">
            <div className="w-full md:w-3/4 mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {sediPageData?.activeCourses?.map((item, index) => {
                  return (
                    <CoursesCard
                      key={index}
                      typeTwo={true}
                      icon={item?.icon?.webp || item?.icon?.url}
                      title={item?.subtitle?.replace(
                        item?.subtitle?.split(" ").pop(),
                        ""
                      )}
                      type={item?.subtitle?.split(" ").pop()}
                      bgColor={item?.boxBackgroundColor?.hex}
                      textColor={item?.buttonDefaultColor?.hex}
                      defaultColor={item?.buttonDefaultColor?.hex}
                      hoverColor={item?.buttonHoverColor?.hex}
                      pressedColor={item?.buttonPressedColor?.hex}
                      showDescText={true}
                      asLink={`/offerta/corsi/${item?.title
                        ?.toLowerCase()
                        ?.replace(/ +/g, "-")}`}
                      url={`/offerta/[slug]/[typology]`}
                      labelsList={labelsList}
                      btnClass="mt-4"
                      titleClass="mt-4"
                      isSediPage={true}
                      linkClass="stretched-link"
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </section>

      <section>
        <div className="container">
          <div className="w-full md:w-3/4 mx-auto">
            <SeparatorLine />
            <div className="grid grid-cols-1">
              <div>
                <h2 className="h2-web font-extrabold mb-8">
                  {sediPageData?.attachmentSectionTitle}
                </h2>
                <ul className="mb-16">
                  {sediPageData?.attachments?.length > 0 &&
                    sediPageData?.attachments?.map((item, index) => {
                      return (
                        <li className="mb-3 last:mb-0" key={index}>
                          <span
                            onClick={() => downloadURL(item?.url)}
                            className="text-theme font-bold underline cursor-pointer  "
                          >
                            {item?.title}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          <TwoColumnIconBoxCardWithImage
            pageBoxesList={sediPageData?.pageBoxesList}
            labelsList={labelsList}
          />
        </div>
      </section>

      <section>
        <TwoColumnGradientCard
          standardPage={pageData?.sediDetailsData?.standardPage}
          externalLinkPage={pageData?.sediDetailsData?.externalLinkPage}
          labelsList={labelsList}
        />
      </section>
    </>
  );
}

// const mapStateToProps = (state) => ({
//   pageData: state.sediPage,
//   labelsConfigData: state.labelsConfig,
// });

// const mapDispatchToProps = {
//   fetchSediDetailsData,
// };

// export default connect(mapStateToProps, mapDispatchToProps)(LocationDetails);

export const getServerSideProps = async ({
  preview = false,
  locale = "en",
  query,
}) => {
  const { id } = query;

  if (id) {
    let requestParams = SEDI_DETAIL_QUERY;
    requestParams = requestParams?.replace(/\$id/g, `"${id}"`);
    requestParams = requestParams?.replace(/\lang/g, locale);

    const sediResponse = await store.dispatch(
      fetchSediDetailsData(requestParams, preview)
    );

    const labelsConfigData = await store.dispatch(
      fetchLabelsAndConfigData(LABEL_AND_CONFIG_QUERY, preview, {
        lang: locale,
      })
    );

    return {
      props: {
        preview,
        sediPageData: sediResponse?.premise || {},
        pageData: { sediDetailsData: sediResponse },
        labelsConfigData: labelsConfigData || {},
      },
    };
  }
  return {
    props: {
      preview,
      sediPageData: {},
      pageData: {},
      noData: true,
    },
  };
};
